import { Herinnering } from "@/entities/herinnering/domain.ts";
import { useDeleteHerinnering, useGetHerinneringen, usePostHerinnering } from "@/entities/herinnering/hooks.ts";
import { useDossierId } from "@/features/dossier-details";
import { NewHerinnering } from "@/features/herinneringen/components/new-herinnering.tsx";
import {
    Button,
    dismissToast,
    ErrorDisplay,
    Icon,
    SidebarGroupContent,
    SidebarGroupLabel,
    TimelineBorder,
    TimelineEndIcon,
    toastWithUndo,
} from "@/shared/components";
import {
    TimelineDate,
    TimelineDescription,
    TimelineEntries,
    TimelineEvent,
    TimelineItem,
    TimelineMarker,
    TimelineTime,
} from "@/shared/components/ui/timeline";
import { formatDateTimeRelative } from "@/shared/utils";
import { formatISO } from "date-fns";

export function Herinneringen() {
    const dossierId = useDossierId();
    const { data: herinneringen, error } = useGetHerinneringen(dossierId);
    const deleteHerinneringMutation = useDeleteHerinnering();
    const createHerinneringMutation = usePostHerinnering();

    const deleteHerinnering = (herinnering: Herinnering) => {
        deleteHerinneringMutation.mutate({ dossierId, herinneringId: herinnering.id });

        // Show toast with undo functionality
        toastWithUndo("Herinnering verwijderd", "De herinnering is verwijderd", (id: string | number) => {
            createHerinneringMutation.mutate(
                {
                    dossierId,
                    scheduledAt: herinnering.scheduledAt,
                    tekst: herinnering.titel,
                },
                { onSuccess: () => dismissToast(id) }
            );
        });
    };

    if (error) {
        return <ErrorDisplay error={error} variant="section" title="Fout bij het laden van herinneringen" />;
    }

    return (
        <article className="relative">
            <SidebarGroupLabel className="text-m leading-l mt-2 mb-2 grid grid-cols-[48px_40px_1fr] gap-y-3 px-0 font-normal">
                <Icon name="plus-circle" className="bg-default col-start-2 justify-self-center" />
                <NewHerinnering dossierId={dossierId} />
            </SidebarGroupLabel>
            <SidebarGroupContent>
                <TimelineEntries className="grid gap-2" data-testid="herinneringen-timeline">
                    {herinneringen?.map((herinnering, idx) => {
                        const [time, date] = formatDateTimeRelative(herinnering.scheduledAt).split(",");

                        return (
                            <TimelineItem key={herinnering.id}>
                                <TimelineTime dateTime={formatISO(herinnering.scheduledAt)}>{time}</TimelineTime>
                                <TimelineMarker filled={false} />
                                <TimelineDate>{date}</TimelineDate>
                                <TimelineEvent>{herinnering.titel}</TimelineEvent>
                                <TimelineDescription className="text-s text-content-subtle">Herinnering</TimelineDescription>
                                <TimelineEndIcon>
                                    <Button
                                        variant="tertiary"
                                        iconOnly
                                        size="s"
                                        icon="close"
                                        onClick={() => deleteHerinnering(herinnering)}
                                    />
                                </TimelineEndIcon>
                                <TimelineBorder showBorder={idx !== herinneringen.length - 1} />
                            </TimelineItem>
                        );
                    })}
                </TimelineEntries>
            </SidebarGroupContent>
        </article>
    );
}
